import { useEffect, useState } from "react";
import GalleryLightbox from "../components/GalleryLightbox";
import CtaBanner from "../components/CtaBanner";
import { PROJECTS_GALLERY } from "../data/siteData";

function BeforeAfterSlider({ beforeImage, afterImage, alt = "Før og efter" }) {
  const [sliderPos, setSliderPos] = useState(50);

  return (
    <div className="relative w-full aspect-[4/3] sm:aspect-[16/10] overflow-hidden rounded-xl border border-stone-200 select-none group">
      {/* After Image (Background) */}
      <img
        src={afterImage}
        alt={`${alt} - Efter`}
        className="absolute inset-0 w-full h-full object-cover"
        loading="lazy"
      />

      {/* Before Image (Top layer clipped) */}
      <img
        src={beforeImage}
        alt={`${alt} - Før`}
        className="absolute inset-0 w-full h-full object-cover"
        style={{ clipPath: `inset(0 ${100 - sliderPos}% 0 0)` }}
        loading="lazy"
      />

      {/* Vertical Divider Line & Drag Knob */}
      <div
        className="absolute inset-y-0 w-1 bg-white shadow-md cursor-ew-resize z-10 flex items-center justify-center pointer-events-none"
        style={{ left: `calc(${sliderPos}% - 2px)` }}
      >
        <div className="w-9 h-9 bg-white text-stone-900 rounded-full shadow-lg border-2 border-[#ea580c] flex items-center justify-center text-xs font-bold gap-0.5 transform transition-transform group-hover:scale-110">
          <i className="fa-solid fa-chevron-left text-[10px] text-[#ea580c]" />
          <i className="fa-solid fa-chevron-right text-[10px] text-[#ea580c]" />
        </div>
      </div>

      {/* Invisible Interactive Range Input Overlay */}
      <input
        type="range"
        min="0"
        max="100"
        value={sliderPos}
        onChange={(e) => setSliderPos(Number(e.target.value))}
        aria-label="Før og efter sammenligning slider"
        className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize z-20"
      />

      {/* Labels */}
      <span className="absolute bottom-3 left-3 bg-stone-900/85 backdrop-blur-xs text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-md z-10 pointer-events-none shadow-xs">
        Før
      </span>
      <span className="absolute bottom-3 right-3 bg-[#ea580c]/90 backdrop-blur-xs text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-md z-10 pointer-events-none shadow-xs">
        Efter
      </span>
    </div>
  );
}

export default function ReferencesPage() {
  const [galleries, setGalleries] = useState([]);
  const [activeCategory, setActiveCategory] = useState("Alle");

  useEffect(() => {
    document.title = "Referencer & Galleri - Kærby Tømrer- & Snedkerforretning ApS";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "Se udvalgte projekter og referencer udført af Kærby Tømrer- & Snedkerforretning ApS. Træterrasser, tilbygninger, vinduer og snedkerarbejde."
      );
    }

    // Fetch galleries from CMS
    fetch("https://api.dev.auxo.dk/public/v2/content/galleries")
      .then((res) => res.json())
      .then((data) => {
        if (data && data.items) {
          setGalleries(data.items);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <div className="bg-white min-h-screen space-y-0 text-stone-900">
      
      {/* Header */}
      <section className="relative bg-zinc-900 text-white pt-20 sm:pt-24 pb-16 sm:pb-20 border-b border-zinc-800 -mt-14 sm:-mt-16 z-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-3">
          <span className="text-[#ea580c] font-bold text-xs uppercase tracking-widest bg-zinc-950 px-3.5 py-1 rounded-full inline-block border border-zinc-800">
            Vores Billedarkiv
          </span>
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight uppercase">
            Referencer & Billedgalleri
          </h1>
          <p className="text-stone-300 text-base sm:text-lg max-w-2xl">
            Gå på opdagelse i vores udførte projekter. Klik på billederne for at se dem i forstørret format.
          </p>
        </div>
      </section>

      {/* Featured Before / After Projects */}
      <section className="py-16 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          <div className="space-y-2">
            <h2 className="text-3xl font-extrabold uppercase text-stone-900">
              Før- & Efter Billeder
            </h2>
            <p className="text-stone-700 text-base">
              Sammenligning af byggeprojekter før og efter vores indsats.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {PROJECTS_GALLERY.map((proj, idx) => (
              <div key={idx} className="bg-white p-2.5 sm:p-3 rounded-2xl border border-stone-200 shadow-sm">
                <BeforeAfterSlider
                  beforeImage={proj.beforeImage}
                  afterImage={proj.afterImage}
                  alt={proj.title || "Før og efter projekt"}
                />
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* CMS Galleries Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          
          {/* Sorting / Filter buttons */}
          {(() => {
            const categories = ["Alle", ...galleries.map((g) => g.title).filter(Boolean)];
            const displayImages =
              activeCategory === "Alle"
                ? galleries.flatMap((g) => g.images || [])
                : galleries.find((g) => g.title === activeCategory)?.images || [];

            const fallbackImages = [
              "https://assets.auxodev.dk/assets/c825601b-4884-4adf-bb6b-b4af6f529bc2/12-ipe-terrasse-640x480-c.jpg",
              "https://assets.auxodev.dk/assets/c825601b-4884-4adf-bb6b-b4af6f529bc2/13-ipe-terrasse-640x480-c.jpg",
              "https://assets.auxodev.dk/assets/c825601b-4884-4adf-bb6b-b4af6f529bc2/sko-diske-1-640x480-c.jpg",
              "https://assets.auxodev.dk/assets/c825601b-4884-4adf-bb6b-b4af6f529bc2/creme-fraiche-1-640x480-c.jpg"
            ];

            const imagesToRender = displayImages.length > 0 ? displayImages : fallbackImages;

            return (
              <div className="space-y-8">
                {categories.length > 1 && (
                  <div className="flex flex-wrap items-center justify-center gap-2.5">
                    {categories.map((cat) => {
                      const isActive = activeCategory === cat;
                      return (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => setActiveCategory(cat)}
                          className={`px-5 py-2.5 rounded-md text-xs font-extrabold uppercase tracking-wider transition-all cursor-pointer ${
                            isActive
                              ? "bg-[#ea580c] text-white shadow-xs"
                              : "bg-stone-100 text-stone-700 hover:bg-stone-200 border border-stone-200"
                          }`}
                        >
                          {cat}
                        </button>
                      );
                    })}
                  </div>
                )}

                <GalleryLightbox images={imagesToRender} title="Referencer" />
              </div>
            );
          })()}

        </div>
      </section>

      <CtaBanner />

    </div>
  );
}
